/**
 * Backward-compat shim. The collector logic lives in collectors/* (one file
 * per signal) and core/* (orchestrator, transport). This file re-exports
 * the public surface so older imports `from "./collector"` keep resolving.
 *
 * Prefer importing from "./core/orchestrator", "./core/transport", or
 * specific collectors in new code.
 */

export { collectFingerprintFeatures } from "./core/orchestrator";

import { collectFingerprintFeatures } from "./core/orchestrator";
import {
  postFingerprint,
  readContinuityHintFromStorage,
  persistContinuityToken,
} from "./core/transport";

/**
 * Lower-level helper that collects features and POSTs them to /v1/collect.
 * Most consumers should use `load({ apiEndpoint }).then(fp => fp.get())`
 * instead.
 *
 * `apiEndpoint` is required — the SDK does not fall back to environment
 * variables (those are bundler-specific and would crash when the UMD bundle
 * runs on a third-party site without a Webpack-style shim).
 *
 * Set `allowPlaintext: true` only against an HTTP origin paired with a test
 * environment API key. The server rejects plaintext for live keys.
 */
export async function sendFingerprint(opts: {
  apiEndpoint: string;
  debug?: boolean;
  allowPlaintext?: boolean;
}) {
  const { apiEndpoint, debug = false, allowPlaintext = false } = opts;
  if (!apiEndpoint || typeof apiEndpoint !== "string") {
    throw new Error("[fp] sendFingerprint: `apiEndpoint` is required");
  }
  const features = await collectFingerprintFeatures();
  const continuity_hint = readContinuityHintFromStorage();
  const payload: Record<string, unknown> = { features, debug };
  if (continuity_hint) payload.continuity_hint = continuity_hint;

  // Lazy import keeps crypto out of bundles for environments that don't use it.
  const { encryptPayload } = await import("./crypto");
  const encryptedBody = await encryptPayload(JSON.stringify(payload));

  if (encryptedBody.v === 0 && !allowPlaintext) {
    throw new Error(
      "[fp] crypto.subtle unavailable — refusing to send plaintext payload. Use HTTPS, or pass allowPlaintext: true with a test API key.",
    );
  }

  const response = await postFingerprint({
    endpoint: `${apiEndpoint}/v1/collect`,
    body: JSON.stringify(encryptedBody),
  });

  if (!response.ok) {
    let message = `API ${response.status}`;
    try {
      const parsed = JSON.parse(response.text) as { message?: string };
      message = `API ${response.status}: ${parsed.message || response.text}`;
    } catch {
      message = `API ${response.status}: ${response.text}`;
    }
    throw new Error(message);
  }

  let data: { continuity?: { visitor_id: string; issued_at: number; sig: string } } = {};
  try {
    data = JSON.parse(response.text);
  } catch {
    /* tolerate empty/non-JSON response */
  }
  if (data?.continuity?.visitor_id && data.continuity.sig) {
    persistContinuityToken(data.continuity);
  }
  return data;
}
